import { ACTIVE_WINDOW_DAYS, isPlayerActiveAsOf, LEADERBOARD_SCOPES } from './leaderboardPlayerScope';

/**
 * Maps a URL-level gameType param to the leaderboard_snapshots.ladder_type value.
 * The mapping must stay in sync with the ladder_type CHECK constraint in the schema.
 *
 * @param {'mixed'|'levels-male'|'levels-female'|'combined'} gameType
 * @returns {'mixed'|'levels_male'|'levels_female'|'combined'|null}
 */
export const gameTypeToLadderType = (gameType) => {
  switch (gameType) {
    case 'mixed':         return 'mixed';
    case 'levels-male':   return 'levels_male';
    case 'levels-female': return 'levels_female';
    case 'combined':      return 'combined';
    default:              return null;
  }
};

/**
 * Builds a Map from player_id → { rank_delta, elo_delta } from a latest-snapshot
 * RPC result.  Only rank_delta and elo_delta are included — never snapshot.rank or
 * snapshot.elo_rating, which must not overwrite the live PCR-derived values.
 *
 * @param {Array} snapshotRows  Result of getLatestLeaderboardSnapshot()
 * @returns {Map<string, {rank_delta: number|null, elo_delta: number|null}>}
 */
export const buildSnapshotMovementMap = (snapshotRows) => {
  const map = new Map();
  (snapshotRows ?? []).forEach((row) => {
    map.set(row.player_id, {
      rank_delta: row.rank_delta ?? null,
      elo_delta:  row.elo_delta ?? null,
    });
  });
  return map;
};

/**
 * Filters snapshot entries to players active as of referenceDate and assigns ranks.
 *
 * @param {Array<{ player_id: string, elo_rating: number }>} entries
 * @param {Record<string, string[]>} sessionDatesByPlayerId - ISO session dates per player
 * @param {Date|string} referenceDate
 * @param {number} [windowDays=ACTIVE_WINDOW_DAYS]
 * @returns {Map<string, { rank: number, elo_rating: number }>}
 */
export const rankActiveSnapshotSubset = (
  entries,
  sessionDatesByPlayerId,
  referenceDate,
  windowDays = ACTIVE_WINDOW_DAYS
) => {
  const ref = new Date(referenceDate);
  const activeEntries = (entries ?? []).filter((row) => {
    const dates = sessionDatesByPlayerId[row.player_id] ?? [];
    const lastBeforeRef = dates
      .map((d) => new Date(d))
      .filter((d) => !Number.isNaN(d.getTime()) && d <= ref)
      .sort((a, b) => b - a)[0];
    if (!lastBeforeRef) return false;
    return isPlayerActiveAsOf(lastBeforeRef.toISOString(), ref, windowDays);
  });

  const sorted = [...activeEntries].sort((a, b) => {
    const eloDiff = (b.elo_rating ?? 0) - (a.elo_rating ?? 0);
    if (eloDiff !== 0) return eloDiff;
    return (a.player_id ?? '').localeCompare(b.player_id ?? '');
  });

  const rankMap = new Map();
  sorted.forEach((row, index) => {
    rankMap.set(row.player_id, {
      rank: index + 1,
      elo_rating: row.elo_rating,
    });
  });
  return rankMap;
};

/**
 * Derives active-scoped rank movement from the two most recent snapshots.
 *
 * @param {Object} params
 * @param {Array} params.currentSnapshotEntries
 * @param {Array} params.previousSnapshotEntries
 * @param {string|Date} params.currentSnapshotAt
 * @param {string|Date} params.previousSnapshotAt
 * @param {Record<string, string[]>} params.sessionDatesByPlayerId
 * @returns {Map<string, { rank_delta: number|null, elo_delta: number|null }>}
 */
export const computeScopedMovement = ({
  currentSnapshotEntries,
  previousSnapshotEntries,
  currentSnapshotAt,
  previousSnapshotAt,
  sessionDatesByPlayerId,
}) => {
  const currentRanks = rankActiveSnapshotSubset(
    currentSnapshotEntries,
    sessionDatesByPlayerId,
    currentSnapshotAt
  );
  const previousRanks = rankActiveSnapshotSubset(
    previousSnapshotEntries,
    sessionDatesByPlayerId,
    previousSnapshotAt
  );

  const prevEloByPlayer = new Map(
    (previousSnapshotEntries ?? []).map((row) => [row.player_id, row.elo_rating])
  );
  const currentEloByPlayer = new Map(
    (currentSnapshotEntries ?? []).map((row) => [row.player_id, row.elo_rating])
  );

  const movement = new Map();
  currentRanks.forEach(({ rank }, playerId) => {
    const prev = previousRanks.get(playerId);
    const rankDelta = prev ? prev.rank - rank : null;
    const prevElo = prevEloByPlayer.get(playerId);
    const currentElo = currentEloByPlayer.get(playerId);
    const eloDelta =
      prevElo != null && currentElo != null ? currentElo - prevElo : null;
    movement.set(playerId, { rank_delta: rankDelta, elo_delta: eloDelta });
  });

  return movement;
};

/**
 * Active-cohort rank and movement for one player, using the same re-rank as the
 * club leaderboard Active view. Stored snapshot.rank / rank_delta are full-ladder
 * and must not be shown when the default board is Active.
 *
 * rankDelta is null when the player was not in the previous Active subset ("New").
 *
 * @returns {{ rank: number|null, rankDelta: number|null }|null}
 */
export const activeRankAndDeltaForPlayer = ({
  playerId,
  currentEntries,
  previousEntries,
  currentSnapshotAt,
  previousSnapshotAt,
  sessionDatesByPlayerId,
}) => {
  if (!playerId || !currentSnapshotAt) return null;

  const currentRanks = rankActiveSnapshotSubset(
    currentEntries,
    sessionDatesByPlayerId,
    currentSnapshotAt
  );
  const mine = currentRanks.get(playerId);
  if (!mine) return { rank: null, rankDelta: null };

  if (!previousSnapshotAt || !previousEntries?.length) {
    return { rank: mine.rank, rankDelta: null };
  }

  const movement = computeScopedMovement({
    currentSnapshotEntries: currentEntries,
    previousSnapshotEntries: previousEntries,
    currentSnapshotAt,
    previousSnapshotAt,
    sessionDatesByPlayerId,
  });

  return {
    rank: mine.rank,
    rankDelta: movement.get(playerId)?.rank_delta ?? null,
  };
};

/**
 * Replaces full-ladder rank fields on a session ladder snapshot.
 * Only ladders present in ranksByLadderType are updated.
 *
 * @param {Object} ladderSnapshot
 * @param {Record<string, { rank: number|null, rankDelta: number|null }>} ranksByLadderType
 */
export const applyActiveScopedRanks = (ladderSnapshot, ranksByLadderType) => {
  if (!ladderSnapshot) return ladderSnapshot;

  const apply = (ladder) => {
    if (!ladder?.key) return;
    const scoped = ranksByLadderType?.[ladder.key];
    if (!scoped) return;
    ladder.rank = scoped.rank ?? null;
    ladder.rankDelta = scoped.rankDelta ?? null;
  };

  apply(ladderSnapshot.mixed);
  apply(ladderSnapshot.levels);
  return ladderSnapshot;
};

/**
 * Returns the movement map appropriate for the selected scope.
 *
 * @param {'active'|'all'} scope
 * @param {Object|null} recentSnapshots - { current, previous } from getRecentLeaderboardSnapshots
 * @param {Record<string, string[]>} sessionDatesByPlayerId
 */
export const buildMovementMapForScope = (
  scope,
  recentSnapshots,
  sessionDatesByPlayerId
) => {
  if (scope === LEADERBOARD_SCOPES.ALL) {
    return buildSnapshotMovementMap(recentSnapshots?.current?.entries ?? []);
  }

  if (
    !recentSnapshots?.current?.entries?.length ||
    !recentSnapshots?.previous?.entries?.length
  ) {
    return buildSnapshotMovementMap(recentSnapshots?.current?.entries ?? []);
  }

  return computeScopedMovement({
    currentSnapshotEntries: recentSnapshots.current.entries,
    previousSnapshotEntries: recentSnapshots.previous.entries,
    currentSnapshotAt: recentSnapshots.current.snapshot_at,
    previousSnapshotAt: recentSnapshots.previous.snapshot_at,
    sessionDatesByPlayerId,
  });
};

/** Snapshot history depth needed for movement under each scope. */
export const snapshotFetchLimitForScope = (scope) =>
  scope === LEADERBOARD_SCOPES.ALL ? 1 : 2;

/**
 * Collects unique player IDs from one or more recent-snapshot results.
 *
 * @param {Array<{ current?: { entries?: Array }, previous?: { entries?: Array } }|null>} snapshotsList
 * @returns {string[]}
 */
export const collectPlayerIdsFromRecentSnapshots = (snapshotsList) => {
  const ids = new Set();
  (snapshotsList ?? []).forEach((snap) => {
    (snap?.current?.entries ?? []).forEach((row) => {
      if (row.player_id) ids.add(row.player_id);
    });
    (snap?.previous?.entries ?? []).forEach((row) => {
      if (row.player_id) ids.add(row.player_id);
    });
  });
  return [...ids];
};
